import { useState, type FormEvent } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { AuthLayout, Field } from '@/components/AuthLayout'
import { useAuth } from '@/store/auth'

export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const token = params.get('token')
  const { error, resetPassword } = useAuth()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [mismatch, setMismatch] = useState(false)

  if (!token) {
    return (
      <AuthLayout title="Invalid link" subtitle="This password reset link is missing its token." assurance="This reset link is no longer valid" assuranceTone="caution" backTo="/login">
        <Link
          to="/forgot-password"
          className="block w-full rounded-md bg-brand py-3 text-center text-[16px] font-extrabold text-white shadow-[0_14px_28px_rgba(15,118,110,.24)] transition hover:bg-brand-dark"
        >
          Request a new link
        </Link>
      </AuthLayout>
    )
  }

  if (done) return <Navigate to="/login" replace />

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (password !== confirm) {
      setMismatch(true)
      return
    }
    setMismatch(false)
    setSubmitting(true)
    try {
      await resetPassword(token, password)
      setDone(true)
    } catch {
      // The store surfaces the message; nothing else to do here.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout title="Choose a new password" subtitle="Make it at least 8 characters." assurance="Secure password reset" backTo="/login">
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="New password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          placeholder="At least 8 characters"
          required
          adornment={
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="rounded-md p-1 text-brand-muted transition hover:text-brand-heading"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
        />

        <Field
          label="Confirm password"
          type={showPassword ? 'text' : 'password'}
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          placeholder="Repeat the password"
          required
        />

        {(mismatch || error) && (
          <p
            role="alert"
            className="rounded-lg border border-danger/25 bg-danger/[0.06] px-3 py-2.5 text-[13px] leading-relaxed text-danger"
          >
            {mismatch ? 'Passwords do not match.' : error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-brand py-3 text-[16px] font-extrabold text-white shadow-[0_14px_28px_rgba(15,118,110,.24)] transition hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Saving…' : 'Reset password'}
        </button>
      </form>
    </AuthLayout>
  )
}
